import axiosInstance from '../utils/axiosInstance';
import config from '../config';

const base = 'api/drive/';

export const ROLES = {
  VIEWER: 10,
  EDITOR: 20,
  MANAGER: 30,
  OWNER: 40,
};

export const ROLE_LABELS = {
  10: 'Viewer',
  20: 'Editor',
  30: 'Manager',
  40: 'Owner',
};

const data = (promise) => promise.then((res) => res.data);

/** Turn a server path ("/api/drive/dl/…") into a URL the browser can open. */
export const absoluteApiUrl = (path) => {
  const root = config.apiUrl.endsWith('/') ? config.apiUrl : `${config.apiUrl}/`;
  return `${root}${path.replace(/^\//, '')}`;
};

const driveApi = {
  drives: () => data(axiosInstance.get(`${base}drives/`)),
  createDrive: (name, color) => data(axiosInstance.post(`${base}drives/`, { name, color })),
  updateDrive: (driveId, body) => data(axiosInstance.patch(`${base}drives/${driveId}/`, body)),
  contents: (driveId, folderId) =>
    data(axiosInstance.get(`${base}drives/${driveId}/contents/`, { params: folderId ? { folder: folderId } : {} })),

  node: (id) => data(axiosInstance.get(`${base}nodes/${id}/`)),
  rename: (id, name) => data(axiosInstance.patch(`${base}nodes/${id}/`, { name })),
  // `drive` only matters when `parent` is empty: the root of that drive
  move: (id, parent, drive) => data(axiosInstance.patch(`${base}nodes/${id}/`, drive ? { parent, drive } : { parent })),
  trash: (id) => axiosInstance.delete(`${base}nodes/${id}/`),
  restore: (id) => data(axiosInstance.post(`${base}nodes/${id}/restore/`)),
  purge: (id) => axiosInstance.delete(`${base}nodes/${id}/purge/`),
  star: (id) => axiosInstance.post(`${base}nodes/${id}/star/`),
  unstar: (id) => axiosInstance.delete(`${base}nodes/${id}/star/`),

  createFolder: (drive, parent, name) => data(axiosInstance.post(`${base}folders/`, { drive, parent, name })),
  upload: ({ drive, parent, file, relativePath, onProgress, signal }) => {
    const form = new FormData();
    form.append('drive', drive);
    if (parent) form.append('parent', parent);
    if (relativePath) form.append('relative_path', relativePath);
    form.append('file', file);
    return data(axiosInstance.post(`${base}upload/`, form, {
      signal,
      onUploadProgress: (e) => onProgress?.(e.total ? e.loaded / e.total : 0),
    }));
  },

  downloadSummary: (ids) => data(axiosInstance.post(`${base}download-link/`, { ids, summary: true })),
  downloadUrl: async (ids, inline = false) => {
    const { url } = await data(axiosInstance.post(`${base}download-link/`, { ids, inline }));
    return absoluteApiUrl(url);
  },

  search: (q) => data(axiosInstance.get(`${base}search/`, { params: { q } })),
  recent: () => data(axiosInstance.get(`${base}recent/`)),
  starred: () => data(axiosInstance.get(`${base}starred/`)),
  sharedWithMe: () => data(axiosInstance.get(`${base}shared-with-me/`)),
  trashList: () => data(axiosInstance.get(`${base}trash/`)),
  emptyTrash: (ids) => data(axiosInstance.post(`${base}trash/empty/`, ids ? { ids } : {})),

  access: (nodeId) => data(axiosInstance.get(`${base}nodes/${nodeId}/access/`)),
  driveAccess: (driveId) => data(axiosInstance.get(`${base}drives/${driveId}/access/`)),
  grant: (nodeId, body) => data(axiosInstance.post(`${base}nodes/${nodeId}/access/`, body)),
  grantDrive: (driveId, body) => data(axiosInstance.post(`${base}drives/${driveId}/access/`, body)),
  changeAccess: (entryId, role) => data(axiosInstance.patch(`${base}access/${entryId}/`, { role })),
  revokeAccess: (entryId) => axiosInstance.delete(`${base}access/${entryId}/`),

  security: (nodeId, body) => data(axiosInstance.patch(`${base}nodes/${nodeId}/security/`, body)),
  audit: (params) => data(axiosInstance.get(`${base}audit/`, { params })),
  directory: (q) => data(axiosInstance.get(`${base}directory/`, { params: { q } })),

  // Guest links (external sharing)
  nodeLinks: (nodeId) => data(axiosInstance.get(`${base}nodes/${nodeId}/links/`)),
  createLink: (nodeId, body) => data(axiosInstance.post(`${base}nodes/${nodeId}/links/`, body)),
  updateLink: (linkId, body) => data(axiosInstance.patch(`${base}links/${linkId}/`, body)),
  revokeLink: (linkId) => axiosInstance.delete(`${base}links/${linkId}/`),
  myLinks: () => data(axiosInstance.get(`${base}links/`)),
};

export const adminApi = {
  usage: () => data(axiosInstance.get('api/admin/usage/')),
  people: () => data(axiosInstance.get('api/admin/people/')),
  invite: (body) => data(axiosInstance.post('api/admin/people/', body)),
  updatePerson: (id, body) => data(axiosInstance.patch(`api/admin/people/${id}/`, body)),
  teams: () => data(axiosInstance.get('api/admin/teams/')),
  createTeam: (body) => data(axiosInstance.post('api/admin/teams/', body)),
  updateTeam: (id, body) => data(axiosInstance.patch(`api/admin/teams/${id}/`, body)),
  deleteTeam: (id) => axiosInstance.delete(`api/admin/teams/${id}/`),
  addMember: (teamId, userId) => axiosInstance.post(`api/admin/teams/${teamId}/members/`, { user_id: userId }),
  removeMember: (teamId, userId) => axiosInstance.delete(`api/admin/teams/${teamId}/members/${userId}/`),
};

export const errorMessage = (err, fallback = 'Something went wrong.') => {
  const body = err?.response?.data;
  if (!body) return fallback;
  if (typeof body === 'string') return fallback;
  if (body.detail) return body.detail;
  const first = Object.values(body)[0];
  return Array.isArray(first) ? first[0] : (typeof first === 'string' ? first : fallback);
};

export default driveApi;
